import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  UserCheck, UserX, Clock, Truck, Wrench, ClipboardCheck,
  Briefcase, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPS = [
  { app_id: 'ridero', name: 'Ridero', icon: Truck, color: '#3B82F6' },
  { app_id: 'mechanic_kiosk', name: 'MechanicKiosk', icon: Wrench, color: '#10B981' },
  { app_id: 'pti_pector', name: 'PTI Pector', icon: ClipboardCheck, color: '#F97316' },
];

export default function UserApprovalPage() {
  const { user } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [jobTitles, setJobTitles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Setup dialog
  const [setupUser, setSetupUser] = useState(null);
  const [selectedJobTitles, setSelectedJobTitles] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pendingRes, titlesRes] = await Promise.all([
        axios.get(`${API}/users/pending`, { withCredentials: true }),
        axios.get(`${API}/job-titles`, { withCredentials: true })
      ]);
      setPendingUsers(pendingRes.data);
      setJobTitles(titlesRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/approve`, {}, { withCredentials: true });
      toast.success('User approved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (userId) => {
    if (!confirm('Are you sure? This will delete the user account.')) return;
    try {
      await axios.post(`${API}/users/${userId}/reject`, {}, { withCredentials: true });
      toast.success('User rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const openSetupDialog = (pendingUser) => {
    setSetupUser(pendingUser);
    setSelectedJobTitles({});
  };

  const handleSetupAndApprove = async () => {
    if (!setupUser) return;
    
    try {
      // Set job titles for each selected app
      for (const [appId, jobTitle] of Object.entries(selectedJobTitles)) {
        if (jobTitle && jobTitle !== 'none') {
          await axios.patch(
            `${API}/users/${setupUser.user_id}/job-title?app_id=${appId}&job_title=${jobTitle}`,
            {},
            { withCredentials: true }
          );
        }
      }
      
      // Approve user
      await axios.post(`${API}/users/${setupUser.user_id}/approve`, {}, { withCredentials: true });
      
      toast.success('User approved and configured!');
      setSetupUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup user');
    }
  };

  if (user?.role_id !== 'master_admin') {
    return (
      <DashboardLayout>
        <Header title="Access Denied" />
        <div className="p-6">
          <Card>
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Master Admin Only</h2>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title="User Approvals" subtitle="Approve new user registrations" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <Card className="border-accent/50 bg-accent/5">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center">
              <Clock className="w-7 h-7 text-accent" />
            </div>
            <div>
              <p className="text-3xl font-bold">{pendingUsers.length}</p>
              <p className="text-muted-foreground">Pending Approvals</p>
            </div>
          </CardContent>
        </Card>

        {/* Pending Users */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Pending Users
            </CardTitle>
            <CardDescription>
              Users who registered via Google and need approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : pendingUsers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No pending approvals</p>
                <p className="text-sm">All users are approved</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Barcode</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((pendingUser) => (
                    <TableRow key={pendingUser.user_id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {pendingUser.picture ? (
                            <img 
                              src={pendingUser.picture} 
                              alt="" 
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                              {pendingUser.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{pendingUser.name}</p>
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{pendingUser.email}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-secondary px-2 py-1 rounded">
                          {pendingUser.barcode}
                        </code>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(pendingUser.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSetupDialog(pendingUser)}
                            data-testid={`setup-${pendingUser.user_id}`}
                          >
                            <Briefcase className="w-4 h-4 mr-1" />
                            Setup & Approve
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(pendingUser.user_id)}
                            data-testid={`approve-${pendingUser.user_id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(pendingUser.user_id)}
                            data-testid={`reject-${pendingUser.user_id}`}
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Setup & Approve Dialog */}
      <Dialog open={!!setupUser} onOpenChange={() => setSetupUser(null)}>
        <DialogContent className="max-w-lg" data-testid="setup-dialog">
          <DialogHeader>
            <DialogTitle>Setup User & Approve</DialogTitle>
            <DialogDescription>
              Assign job titles for {setupUser?.name} before approving
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* User info */}
            <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
              {setupUser?.picture ? (
                <img src={setupUser.picture} alt="" className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-lg font-medium">
                  {setupUser?.name?.charAt(0)}
                </div>
              )}
              <div>
                <p className="font-medium">{setupUser?.name}</p>
                <p className="text-sm text-muted-foreground">{setupUser?.email}</p>
              </div>
            </div>

            {/* Job titles per app */}
            <div className="space-y-4">
              <p className="font-medium">Assign Job Titles</p>
              {APPS.map((app) => {
                const Icon = app.icon;
                const titles = jobTitles[app.app_id] || [];
                
                return (
                  <div key={app.app_id} className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${app.color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: app.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium mb-1">{app.name}</p>
                      <Select
                        value={selectedJobTitles[app.app_id] || ''}
                        onValueChange={(val) => setSelectedJobTitles(prev => ({
                          ...prev,
                          [app.app_id]: val
                        }))}
                      >
                        <SelectTrigger data-testid={`job-title-${app.app_id}`}>
                          <SelectValue placeholder="Select job title (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {titles.map((title) => (
                            <SelectItem key={title} value={title}>
                              {title.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupUser(null)}>Cancel</Button>
            <Button onClick={handleSetupAndApprove} data-testid="confirm-setup-btn">
              <UserCheck className="w-4 h-4 mr-2" />
              Approve User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
